import React, { useState } from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';

import withPopupErrors from '../../../components/HOC/withPopupErrors/withPopupErrors';

import compare from '../../../lib/compare';
import usePagination from '../../../lib/hooks/usePagination';
import { useFilters } from '../../../components/common/Filters/Filters';
import { selectStudents } from '../../../redux/reducers/StudentsReducer';
import { selectGrades } from '../../../redux/reducers/GradesReducer';
import { selectStudentsResults } from '../../../redux/reducers/StudentsResultsReducer';

import Input from '../../../components/common/ui/Inputs/Input';
import ActionsBar from '../../../components/common/ActionsBar/ActionsBar';
import StudentsFilters from '../../../components/teacher/StudentsList/Filters';
import CreateStudentForm from '../../../components/teacher/StudentsList/CreationForm';
import NormsTable from '../../../components/teacher/NormsTable/NormsTable';
import StudentsTable from '../../../components/teacher/StudentsList/Table';
import StudentsDiagram from '../../../components/teacher/StudentsDiagram/StudentsDiagram';
import StudentResultsRenderer from '../../../components/teacher/StudentResults/StudentResultsRenderer';

import plusIcon from '../../../assets/add.svg';
import tableIcon from '../../../assets/table.svg';
import diagramIcon from '../../../assets/diagram.svg';
import downloadIcon from '../../../assets/download_light.svg';
import downloadFile from '../../../lib/downloadFile';
import { apiHost } from '../../../config';

interface Props {
  showError: (text: string) => void;
  unauthorizedCallback: () => void;
}

const StudentsPage = ({ showError, unauthorizedCallback }: Props) => {
  const match = useRouteMatch();
  const history = useHistory();

  const [recordsToBeDisplayed, setRecordsToBeDisplayed] = useState(10);
  const [showCreateWindow, setShowCreateWindow] = useState(false);
  const [filters, setFilter] = useFilters({
    username: '',
    sex: '',
    age: '',
    grade: '',
    gradeLetter: '',
    orphan: '',
    visionImpairment: '',
    hearingImpairment: '',
    musculoskeletalImpairment: '',
    doneTesting: '',
    result: '',
  });

  const [sortData, setSortData] = useState({
    column: 'ID',
    descending: false,
  });

  const grades = useSelector(selectGrades);
  const students = useSelector(selectStudents);
  const studentsResults = useSelector(selectStudentsResults);
  const studentsResultsArray = Object.values(
    useSelector(selectStudentsResults),
  );

  const filteredAndSortedStudents = Object.values(students)
    .filter(
      (student) =>
        new RegExp(filters.username, 'i').test(student.username) &&
        new RegExp(filters.sex, 'i').test(student.sex) &&
        new RegExp(filters.age, 'i').test(student.age.toString(10)) &&
        new RegExp(filters.grade, 'i').test(student.grade?.name) &&
        new RegExp(filters.gradeLetter, 'i').test(student.grade?.letter) &&
        new RegExp(filters.orphan, 'i').test(
          (student?.orphan ? 1 : 0).toString(),
        ) &&
        new RegExp(filters.visionImpairment, 'i').test(
          (student?.visionImpairment ? 1 : 0).toString(),
        ) &&
        new RegExp(filters.hearingImpairment, 'i').test(
          (student?.hearingImpairment ? 1 : 0).toString(),
        ) &&
        new RegExp(filters.musculoskeletalImpairment, 'i').test(
          (student?.musculoskeletalImpairment ? 1 : 0).toString(),
        ) &&
        new RegExp(filters.doneTesting, 'i').test(
          (student?.doneTesting ? 1 : 0).toString(),
        ) &&
        new RegExp(filters.result, 'i').test(
          (studentsResults?.[student.id]?.result ?? 0).toString(),
        ),
    )
    .sort((first, second) => {
      const comparedUsernameLength = compare(
        first.username.length,
        second.username.length,
      );

      switch (sortData.column) {
        case 'ID':
          if (comparedUsernameLength !== 0) return comparedUsernameLength;
          return compare(first.username, second.username);
        case 'Пол':
          return compare(first.sex, second.sex);
        case 'Возраст':
          return compare(first.age, second.age);
        case 'Сирота':
          return compare(first.orphan, second.orphan);
        case 'НЗ':
          return compare(first.visionImpairment, second.visionImpairment);
        case 'НС':
          return compare(first.hearingImpairment, second.hearingImpairment);
        case 'НОДА':
          return compare(first.musculoskeletalImpairment, second.musculoskeletalImpairment);
        case 'Прошёл':
          return compare(first.doneTesting, second.doneTesting);
        case 'КВЕРИПО':
          return compare(
            studentsResults?.[first.id]?.KVERIPO ?? 0,
            studentsResults?.[second.id]?.KVERIPO ?? 0,
          );
        case 'ИРП':
          return compare(
            studentsResults?.[first.id]?.IRP ?? 0,
            studentsResults?.[second.id]?.IRP ?? 0,
          );
        case 'Вероятность проявлений рискового поведения':
          console.log(studentsResults?.[first.id] ?? 0);
          return compare(
            studentsResults?.[first.id]?.result ?? 0,
            studentsResults?.[second.id]?.result ?? 0,
          );
        default:
          return compare(first.username, second.username);
      }
    });

  if (sortData.descending) filteredAndSortedStudents.reverse();

  const { paginator, page } = usePagination(
    Math.ceil(filteredAndSortedStudents.length / recordsToBeDisplayed),
  );

  const studentsForRender = filteredAndSortedStudents.slice(
    (page - 1) * recordsToBeDisplayed,
    page * recordsToBeDisplayed,
  );

  const studentsResultsForRender = studentsResultsArray.filter(
    ({ userId }) =>
      filteredAndSortedStudents.find(({ id }) => id === userId) !== undefined,
  );

  const actionsBar = (
    <ActionsBar
      actionButtons={[
        {
          src: plusIcon,
          alt: 'Добавить ученика',
          placement: 'right',
          onClick: () => setShowCreateWindow(true),
        },
        {
          src: tableIcon,
          alt: 'Список учеников',
          placement: 'center',
          onClick: () => history.push(`${match.url}`),
        },
        {
          src: diagramIcon,
          alt: 'Статистика',
          placement: 'center',
          onClick: () => history.push(`${match.url}/diagram`),
        },
        {
          src: downloadIcon,
          alt: 'Выгрузить в Excel',
          placement: 'right',
          onClick: () =>
            downloadFile(
              `${apiHost}/api/teacher/users/xlsx/results?${Object.entries(
                filters,
              )
                .map(([key, value]) => `${key}=${value}`)
                .join('&')}`,
              'Результаты.xlsx',
            ),
        },
      ]}
    />
  );

  const recordsToBeDisplayedInput = (
    <div className="d-flex align-items-center">
      <label htmlFor="records-to-be-displayed-input">
        Кол-во записей на одной странице
      </label>

      <Input
        id="records-to-be-displayed-input"
        style={{
          height: 37,
          width: 70,
          marginLeft: 15,
          textAlign: 'center',
        }}
        value={recordsToBeDisplayed}
        onChange={(value) => setRecordsToBeDisplayed(parseInt(value, 10) || 0)}
      />
    </div>
  );

  return (
    <>
      <Switch>
        <Route path={`${match.url}`} exact>
          <div style={{ display: 'flex', gap: '5rem' }}>
            <div style={{ width: '45vw', minWidth: '20rem' }}>
              <div
                className="d-flex align-items-start"
                style={{ height: '80px' }}
              >
                {recordsToBeDisplayedInput}
                <div className="flex-grow-1">{actionsBar}</div>
              </div>

              <NormsTable />
              <br />

              <StudentsTable
                students={studentsForRender}
                studentsResults={studentsResults}
                grades={grades}
                unauthorizedCallback={unauthorizedCallback}
                showError={showError}
                sortData={sortData}
                setSortData={setSortData}
              />

              {paginator}
            </div>

            <StudentsFilters filters={filters} setFilter={setFilter} />
          </div>
        </Route>

        <Route path={`${match.url}/diagram`} exact>
          <div style={{ display: 'flex', gap: '5rem' }}>
            <div style={{ width: '45vw', minWidth: '20rem' }}>
              <div
                className="d-flex align-items-start"
                style={{ height: '80px' }}
              >
                <div className="flex-grow-1">{actionsBar}</div>
              </div>

              <StudentsDiagram studentsResults={studentsResultsForRender} />
            </div>

            <StudentsFilters filters={filters} setFilter={setFilter} />
          </div>
        </Route>

        <Route path={`${match.url}/:userId`}>
          <StudentResultsRenderer />
        </Route>
      </Switch>

      {showCreateWindow && (
        <CreateStudentForm onClose={() => setShowCreateWindow(false)} />
      )}
    </>
  );
};

export default withPopupErrors<Props>(StudentsPage);
